import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import { Card, Row, Col } from "react-bootstrap";
import {
  FaUsers,
  FaTasks,
  FaClock,
  FaChartBar
} from "react-icons/fa";
import API_BASE_URL from "../config";
import { toast, ToastContainer } from "react-toastify";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend
} from "recharts";

const Dashboard = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("Admin");
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [activeTasks, setActiveTasks] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [timesheetCount, setTimesheetCount] = useState(0);


  const [chartData, setChartData] = useState([]);

  // Colors for charts
  const COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444"];

useEffect(() => {
  const token = sessionStorage.getItem("token");
  const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

  if (!token) {
    toast.error("You must be logged in to access this page.");
    navigate("/");
    return;
  }

  // ✅ check admin role
  if (!roles.includes("ROLE_ADMIN")) {
    toast.error("You are not authorized to access this page.");
    navigate("/");
    return;
  }
}, [navigate]);


  // Fetch employees
 useEffect(() => {
  const token = localStorage.getItem("token"); // ⬅️ get token

  fetch(`${API_BASE_URL}/api/employees/active`, {
    headers: {
      Authorization: `Bearer ${token}`, // ⬅️ add token
      "Content-Type": "application/json",
    },
  })
    .then((res) => res.json())
    .then((data) => setTotalEmployees(data.length))
    .catch(console.error);
}, []);


  // Fetch tasks
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/tasks`, {
      headers: { Authorization: "Bearer " + token }
    })
      .then((res) => res.json())
      .then((data) => {
        const inProgress = data.filter(t => t.status === "IN_PROGRESS").length;
        setActiveTasks(inProgress);
      })
      .catch(console.error);
  }, []);

  // Fetch leaves
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/leaves/all`, {
      headers: { Authorization: "Bearer " + token }
    })
      .then((res) => res.json())
      .then((data) => {
        const count = data.filter(
          l => l.status === "PENDING" || l.status === "FORWARDED"
        ).length;

        setPendingLeaves(count);
      })
      .catch(console.error);
  }, []);

  // Fetch profile
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/dashboard`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.username) setUsername(data.username);
      })
      .catch(console.error);
  }, []);

  // Dynamic chart data
  useEffect(() => {
    setChartData([
      { name: "Employees", value: totalEmployees },
      { name: "Active Tasks", value: activeTasks },
      { name: "Pending Leaves", value: pendingLeaves }
    ]);
  }, [totalEmployees, activeTasks, pendingLeaves]);

  // Fetch timesheets (unique employees logged in last 2 months)
useEffect(() => {
  const token = sessionStorage.getItem("token");
  if (!token) return;

  fetch(`${API_BASE_URL}/api/worklogs`, {
    headers: { Authorization: "Bearer " + token }
  })
    .then((res) => res.json())
    .then((data) => {
      if (!Array.isArray(data)) return;

      const now = new Date();

      // Current month start
      const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Previous month start
      const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      // We need logs from previous month + current month
      const filtered = data.filter((log) => {
        const logDate = new Date(log.date);
        return logDate >= startOfPreviousMonth && logDate <= now;
      });

      // Count unique employeeIds
      const uniqueEmployees = new Set(filtered.map(l => l.employeeId));

      setTimesheetCount(uniqueEmployees.size);
    })
    .catch(console.error);
}, []);


  const summaryCardStyle = {
    cursor: "pointer",
    height: "160px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px"
  };


  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div className="container-fluid mt-4" style={{ paddingTop: "110px", paddingBottom:"50px" }}>
        <h2 className="mb-4 fw-bold dashboard-title">
          👋 Welcome back, {username}
        </h2>

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Link to="/allemployee" style={{ textDecoration: "none" }}>
              <Card className="summary-card" style={summaryCardStyle}>
                <div className="icon-wrapper bg-primary-light">
                  <FaUsers size={30} className="text-primary" />
                </div>
                <h6>Total Employees</h6>
                <h3 className="fw-bold">{totalEmployees}</h3>
              </Card>
            </Link>
          </Col>

          <Col md={3}>
            <Link to="/adminviewtasks" style={{ textDecoration: "none" }}>
              <Card className="summary-card" style={summaryCardStyle}>
                <div className="icon-wrapper bg-success-light">
                  <FaTasks size={30} className="text-success" />
                </div>
                <h6>Ongoing Tasks</h6>
                <h3 className="fw-bold">{activeTasks}</h3>
              </Card>
            </Link>
          </Col>

          <Col md={3}>
            <Link to="/pendingleavelist" style={{ textDecoration: "none" }}>
              <Card className="summary-card" style={summaryCardStyle}>
                <div className="icon-wrapper bg-warning-light">
                  <FaClock size={30} className="text-warning" />
                </div>
                <h6>Pending/Forwarded Leaves for Approval</h6>
                <h3 className="fw-bold">{pendingLeaves} </h3>
              </Card>
            </Link>
          </Col>

          <Col md={3}>
          <Link to="/alltimesheetgrid" style={{ textDecoration: "none" }}>
            <Card className="summary-card" style={summaryCardStyle}>
              <div className="icon-wrapper bg-danger-light">
                <FaChartBar size={30} className="text-danger" />
              </div>
              <h6>Timesheets</h6>
              <h3 className="fw-bold">{timesheetCount}</h3>
            </Card>
            </Link>
          </Col>
        </Row>

        {/* Charts */}
        <Row>
          <Col md={8}>
            <Card className="chart-card">
              <h5 className="fw-bold mb-3">Overview Summary</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#6366F1" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="chart-card">
              <h5 className="fw-bold mb-3">Distribution</h5>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={120}
                    
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>

                  {/* Add this for hover tooltip */}
                  <Tooltip />

                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>

        </Row>
      </div>

      {/* Custom CSS */}
      <style>
        {`
          .dashboard-title {
            font-size: 28px;
            color: #1f2937;
          }

          .summary-card {
            text-align: center;
            padding: 28px;
            border-radius: 12px;
            background: #ffffff;
            transition: 0.3s ease;
            cursor: pointer;
            box-shadow: 0px 4px 12px rgba(0,0,0,0.06);
          }

          .summary-card:hover {
            transform: translateY(-6px);
            box-shadow: 0px 10px 25px rgba(0,0,0,0.15);
          }

          .icon-wrapper {
            width: 55px;
            height: 55px;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 0 auto 12px auto;
            border-radius: 12px;
          }

          .bg-primary-light { background: #eef2ff; }
          .bg-success-light { background: #ecfdf5; }
          .bg-warning-light { background: #fffbeb; }
          .bg-danger-light  { background: #fef2f2; }

          .chart-card {
            padding: 25px;
            border-radius: 14px;
            background: white;
            box-shadow: 0px 4px 14px rgba(0,0,0,0.06);
          }
        `}
      </style>
    </>
  );
};

export default Dashboard;
